import { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi, fmtD } from "@/lib/api";
import { Field, Table, Th, Td, Pill } from "@/components/kit";
import { InviteLink } from "@/components/PlatformTools";

const BTN = "ka-press inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60";
const BLANK = { name: "", email: "", password: "", mode: "invite" };

function AddAdminDialog({ onClose, onAdded }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/admins", { name: f.name, email: f.email, password: f.mode === "password" ? f.password : null });
      toast.success(`${f.name} is now a Super-Admin`);
      onAdded(data.invite_link ? { link: data.invite_link, email: data.email } : null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a Super-Admin</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">They get full control of the platform: every business, packages, payments, coupons and settings.</p>
        <form onSubmit={save} className="grid gap-4">
          <Field label="Name *"><input data-testid="admin-name-input" required className="ka-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Email *"><input data-testid="admin-email-input" type="email" required className="ka-input" value={f.email} onChange={set("email")} /></Field>
          <div className="flex gap-2">{[["invite", "Email an invite link"], ["password", "Set a password now"]].map(([k, l]) => <button type="button" key={k} data-testid={`admin-mode-${k}`} onClick={() => setF({ ...f, mode: k })} className={`ka-press rounded-full px-4 py-1.5 text-xs font-semibold ${f.mode === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}</div>
          {f.mode === "password" && <Field label="Password *" hint="At least 8 characters"><input data-testid="admin-password-input" type="password" autoComplete="new-password" minLength={8} required className="ka-input" value={f.password} onChange={set("password")} /></Field>}
          <button data-testid="admin-save-button" disabled={busy} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Add Super-Admin</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SuperAdmins() {
  const { data, reload } = useApi("/platform/admins");
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState(null);
  const toggle = async (a) => {
    try {
      await api.post(`/platform/admins/${a.id}/status`, { active: !a.active });
      toast.success(a.active ? `Removed access for ${a.name}` : `Restored access for ${a.name}`);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div data-testid="super-admins-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Everyone here can run the whole platform. Removing someone signs them out straight away.</p>
        <button data-testid="add-admin-button" onClick={() => setAdding(true)} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}><Plus className="h-4 w-4" />Add Super-Admin</button>
      </div>
      <Table testid="admins-table">
        <thead><tr><Th>Name</Th><Th>Email</Th><Th>Added</Th><Th>Status</Th><Th className="text-right">Access</Th></tr></thead>
        <tbody>
          {(data || []).map((a) => (
            <tr key={a.id} data-testid={`admin-row-${a.id}`}>
              <Td className="font-semibold text-slate-900">{a.name}{a.is_you && <span className="ml-2 text-xs font-normal text-slate-500">(you)</span>}</Td>
              <Td>{a.email}</Td>
              <Td className="text-xs">{a.created_at ? fmtD(a.created_at) : "—"}{a.added_by && <div className="text-slate-500">by {a.added_by}</div>}</Td>
              <Td><Pill status={!a.active ? "low" : a.invite_pending ? "due" : "ok"} label={!a.active ? "Removed" : a.invite_pending ? "Invite pending" : "Active"} /></Td>
              <Td className="text-right">{!a.is_you && <button data-testid={`admin-toggle-${a.id}`} onClick={() => toggle(a)} className={`ka-press rounded-full border px-3 py-1.5 text-xs font-semibold ${a.active ? "border-rose-200 text-rose-700 hover:border-rose-500" : "border-slate-300 hover:border-slate-900"}`}>{a.active ? "Remove" : "Restore"}</button>}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {adding && <AddAdminDialog onClose={() => setAdding(false)} onAdded={(inv) => { setAdding(false); setInvite(inv); reload(); }} />}
      {invite && <InviteLink link={invite.link} email={invite.email} onClose={() => setInvite(null)} />}
    </div>
  );
}
